/**
 * Password Validation Utilities
 * Centralized password validation logic that can be easily updated
 * without requiring changes to UI components
 */

import { passwordRules } from './validation-rules/password-rules.brand.generated';

export interface PasswordValidationRule {
  id: string;
  label: string; // Fallback label if dictionaryKey is not provided or not found
  dictionaryKey?: string; // Optional dictionary key for i18n
  test: (password: string) => boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  failedRules: string[];
  passedRules: string[];
}

/**
 * Default password validation rules
 * Re-exported from password-rules.brand.generated for backward compatibility
 */
export { passwordRules };

// 128 is a practical upper bound to prevent expensive validation on extremely long passwords.
const MAX_PASSWORD_LENGTH = 128;

/**
 * Validates a password against a set of rules
 * @param password - The password to validate
 * @param rules - Array of validation rules (defaults to defaultPasswordRules)
 * @returns Validation result with passed/failed rules
 */
export function validatePassword(
  password: string,
  rules: PasswordValidationRule[] = passwordRules
): PasswordValidationResult {
  if (password.length > MAX_PASSWORD_LENGTH) {
    return {
      isValid: false,
      failedRules: rules.map((rule) => rule.id),
      passedRules: [],
    };
  }

  const passedRules: string[] = [];
  const failedRules: string[] = [];

  rules.forEach((rule) => {
    if (rule.test(password)) {
      passedRules.push(rule.id);
    } else {
      failedRules.push(rule.id);
    }
  });

  return {
    isValid: failedRules.length === 0,
    passedRules,
    failedRules,
  };
}

/**
 * Gets the status of each validation rule for a password
 * @param password - The password to check
 * @param rules - Array of validation rules (defaults to defaultPasswordRules)
 * @returns Map of rule id -> boolean (true = passed, false = failed)
 */
export function getPasswordRuleStatus(
  password: string,
  rules: PasswordValidationRule[] = passwordRules
): Record<string, boolean> {
  if (password.length > MAX_PASSWORD_LENGTH) {
    const status: Record<string, boolean> = {};
    rules.forEach((rule) => {
      status[rule.id] = false;
    });
    return status;
  }

  const status: Record<string, boolean> = {};

  rules.forEach((rule) => {
    status[rule.id] = rule.test(password);
  });

  return status;
}
